import { useCallback } from "react";

import { AccountViewer } from "components/form/graphql/types";
import useTypedContext from "hooks/useTypedContext";
import useSelfServiceFlags from "hooks/useSelfServiceFlags";
import { Action, Stack } from "types/generated";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";
import useHasStackWriteAccess from "./useHasStackWriteAccess";

const TASK_CREATE_ACCESS_TYPES = [Action.TaskCreate];

const hasTaskCreateAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(TASK_CREATE_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook that returns a function to check if the current user can create tasks on a stack.
 * A user needs write access to create tasks.
 * A user can't create tasks on a disabled stack.
 * A user can't create tasks on a stack locked by another user.
 * Stacks created from a template deployment (blueprintDeployment) cannot have tasks created.
 * @returns {(stack?: Stack) => AccessResult} Function that takes a stack and returns an access AccessResult indicating whether the user can create tasks
 */
const useCanCreateTask = (): ((stack?: Stack) => AccessResult) => {
  const hasStackWriteAccess = useHasStackWriteAccess();
  const { viewer } = useTypedContext(AccountContext);
  const { enableSelfServiceV2 } = useSelfServiceFlags();

  return useCallback(
    (stack?: Stack) => {
      if (!stack) {
        return { result: false };
      }

      const blueprintCheck = checkTemplateDeploymentAccess(enableSelfServiceV2, stack);
      if (blueprintCheck) {
        return blueprintCheck;
      }

      if (stack.isDisabled) {
        return { result: false, message: "You can't create tasks on a disabled stack" };
      }

      if (stack.lockedBy && viewer.id !== stack.lockedBy) {
        return {
          result: false,
          message: "You can't create tasks on a stack locked by another user",
        };
      }

      if (hasStackWriteAccess(stack).result) {
        return { result: true };
      }

      if (hasTaskCreateAccess(viewer, stack.spaceDetails?.id || stack.space)) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [hasStackWriteAccess, viewer, enableSelfServiceV2]
  );
};

export default useCanCreateTask;
